import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import authService, { User, PartnerUser } from '../services/auth';
import { getNavigationForUserType, UserType } from '../utils/navigation';
import NavIcon from './common/NavIcon';
import ThemeToggle from './common/ThemeToggle';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [user, setUser] = useState<User | PartnerUser | null>(null);
  const [userType, setUserType] = useState<UserType>('internal');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }
    
    return () => {
      document.body.classList.remove('drawer-open');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Get user data and type
    const storedUser = authService.getStoredUser();
    const storedUserType = authService.getUserType();
    
    if (storedUser) {
      setUser(storedUser);
      setUserType(storedUserType);
    } else {
      // Try to fetch user data from API
      authService.getCurrentUser().then((userData) => {
        if (userData) {
          setUser(userData);
          setUserType(authService.getUserType());
        } else {
          navigate('/login');
        }
      });
    }
  }, [navigate]);

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mangrove-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Get navigation based on user type
  const navigation = getNavigationForUserType(userType);
  
  // Filter navigation based on permissions (only for internal users)
  const filteredNavigation = userType === 'internal' 
    ? navigation.filter(item => {
        if (!item.permission) return true;
        
        // Support both string and array of permissions (OR logic)
        if (Array.isArray(item.permission)) {
          return item.permission.some(perm => authService.hasPermission(perm));
        }
        
        return authService.hasPermission(item.permission);
      })
    : navigation;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors relative">
        <div className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2">
          <img
            src="/seasoldier-logo.png"
            alt="Seasoldier"
            className="hidden dark:block h-6 w-auto"
          />
          <img
            src="/seasoldier-logo.png"
            alt="Seasoldier"
            className="block dark:hidden h-6 w-auto filter invert"
          />
        </div>
        <div className="max-w-7xl mx-auto pl-4 md:pl-12 sm:pl-16 pr-4 sm:pr-6 lg:pr-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link 
                to={userType === 'partner' ? '/partner/dashboard' : '/dashboard'} 
                className="text-xl font-semibold text-mangrove-800 dark:text-mangrove-400 transition-colors"
              >
                <span className="flex flex-col md:inline-flex md:flex-row md:items-center md:gap-2 align-middle">
                  <span className="md:hidden mb-1">
                    <img
                      src="/seasoldier-logo.png"
                      alt="Seasoldier"
                      className="hidden dark:block h-5 w-auto"
                    />
                    <img
                      src="/seasoldier-logo.png"
                      alt="Seasoldier"
                      className="block dark:hidden h-5 w-auto filter invert"
                    />
                  </span>
                  <span className="leading-none">
                    {userType === 'partner' ? 'Partner Portal' : 'Dashboard Seasoldier'}
                  </span>
                </span>
              </Link>
              
              {/* Desktop Navigation Links */}
              <div className="hidden md:ml-8 md:flex md:space-x-8">
                {filteredNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'border-mangrove-500 text-mangrove-600 dark:text-mangrove-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <ThemeToggle />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">
                  {userType === 'partner' 
                    ? ('organization' in user && user.organization?.contact_person 
                        ? user.organization.contact_person 
                        : 'contact_person' in user && user.contact_person 
                        ? user.contact_person 
                        : 'name' in user ? user.name : 'Partner')
                    : 'name' in user ? user.name : 'Unknown User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {userType === 'partner' 
                    ? 'Partner' 
                    : 'role' in user && user.role?.name ? user.role.name : 'User'}
                </p>
              </div>
              <button 
                onClick={handleLogout}
                className="bg-mangrove-600 hover:bg-mangrove-700 dark:bg-mangrove-700 dark:hover:bg-mangrove-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center space-x-2 md:hidden">
              <ThemeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-mangrove-500 transition-colors"
              >
                <span className="sr-only">Open main menu</span>
                {!mobileMenuOpen ? (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Drawer - Overlay */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop/Overlay */}
            <div 
              className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80 z-40 md:hidden transition-opacity duration-300"
              onClick={() => setMobileMenuOpen(false)}
              style={{ animation: 'fadeIn 0.3s ease-in-out' }}
            />
            
            {/* Drawer */}
            <div 
              className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-xl z-50 md:hidden transform transition-transform duration-300 ease-in-out"
              style={{ animation: 'slideInLeft 0.3s ease-in-out' }}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
                <span className="text-lg font-semibold text-mangrove-800 dark:text-mangrove-400">
                  {userType === 'partner' ? 'Partner Portal' : 'Dashboard Seasoldier'}
                </span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 bg-mangrove-50 dark:bg-gray-700/50">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-mangrove-600 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {userType === 'partner' 
                          ? ('organization' in user && user.organization?.contact_person 
                              ? user.organization.contact_person.charAt(0).toUpperCase()
                              : 'contact_person' in user && user.contact_person 
                              ? user.contact_person.charAt(0).toUpperCase()
                              : 'name' in user ? user.name.charAt(0).toUpperCase() : 'P')
                          : 'name' in user ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {userType === 'partner' 
                        ? ('organization' in user && user.organization?.contact_person 
                            ? user.organization.contact_person 
                            : 'contact_person' in user && user.contact_person 
                            ? user.contact_person 
                            : 'name' in user ? user.name : 'Partner')
                        : 'name' in user ? user.name : 'Unknown User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {userType === 'partner' 
                        ? 'Partner' 
                        : 'role' in user && user.role?.name ? user.role.name : 'User'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                {filteredNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === item.href
                        ? 'bg-mangrove-100 dark:bg-mangrove-900/30 text-mangrove-900 dark:text-mangrove-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <span className={`mr-3 flex-shrink-0 ${
                      location.pathname === item.href
                        ? 'text-mangrove-600'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }`}>
                      <NavIcon icon={item.icon} />
                    </span>
                    {item.name}
                  </Link>
                ))}
              </nav>

              {/* Logout Button */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-mangrove-600 hover:bg-mangrove-700 dark:bg-mangrove-700 dark:hover:bg-mangrove-800 transition-colors"
                >
                  <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
